import { useEffect, useRef } from "react";
import { useBrief, type BriefKind } from "@/site/hooks";
import { useT } from "@/i18n";

/** Intrarea în pâlnie, prin problema vizitatorului.
 *
 *  Secțiunea „What I build" e organizată după ce fac eu: web, desktop,
 *  automatizări. Cineva care se gândește „echipa mea retastează aceleași date
 *  toată ziua" nu se recunoaște în „Automations & pipelines" — trebuie să-și
 *  traducă singur problema în categoria mea, iar mulți nu o fac.
 *
 *  Aici întrebările sunt în cuvintele lui, iar fiecare duce la formular cu
 *  tipul deja ales.
 *
 *  Coloana din dreapta — goală pe restul site-ului — primește un desen de
 *  defect de forjare per replică, cu referință D1–D6 ca pe o planșă. Metafora
 *  e literală: „arată terminat și nu duce nimic" e porozitate, „nu vorbesc
 *  între ele" sunt două fluxuri care nu s-au sudat. Desenele sunt în
 *  src/site/painDiscs.js. */

/* Tipul trimis la /api/brief pentru fiecare întrebare. Rămâne în engleză
   indiferent de limba paginii: serverul validează pe listă închisă, iar o
   etichetă tradusă ajunsă în API ar fi respinsă cu 400. */
const KINDS: BriefKind[] = ["Web app", "Automation", "Automation", "Desktop", "Desktop", "Not sure yet"];

export default function Pains() {
  const t = useT();
  const goPlain = useBrief();
  const bankRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let discs: { destroy: () => void } | null = null;
    let dead = false;
    const root = bankRef.current;
    if (!root) return;
    void import("./painDiscs.js").then(
      ({ initPainDiscs }: { initPainDiscs: (r: unknown) => { destroy: () => void } }) => {
        if (dead) return;
        discs = initPainDiscs(root);
      },
    );
    return () => { dead = true; discs?.destroy(); discs = null; };
  }, []);

  return (
    <div className="painsgrid">
      <div className="painbank" ref={bankRef}>
        {t.pains.items.map((q, i) => (
          <PainRow key={q} q={q} kind={KINDS[i]} i={i} d={t.pains.defects[i]} />
        ))}
      </div>

      <aside className="painsout">
        <h3>{t.pains.outHead}</h3>
        <p>{t.pains.outText}</p>
        <button type="button" className="act act-b act-sm" onClick={goPlain}>
          {t.pains.outBtn} <span aria-hidden="true">→</span>
        </button>
      </aside>
    </div>
  );
}

function PainRow({ q, kind, i, d }: { q: string; kind: BriefKind; i: number; d: { n: string; leg: string } }) {
  // Hook-ul se cheamă per rând: fiecare are propriul tip preselectat, iar
  // regulile hook-urilor nu permit apeluri într-un map.
  const go = useBrief(kind);
  const ref = `D${i + 1}`;
  return (
    <div className="painrow">
      <button type="button" className="pain" onClick={go}>
        <span className="painq">{q}</span>
      </button>

      <div className="paindisc">
        <span className="painlead" aria-hidden="true" />
        {/* Discul e decor explicativ, nu o a doua acțiune: nu e focusabil, iar
            numele defectului și legenda sunt text real lângă el. */}
        <span className="paind" data-d={i} aria-hidden="true">
          <canvas className="paindcv" width={112} height={112} aria-hidden="true" />
        </span>
        <span className="paindlab">
          <span className="paindref">{ref}</span>
          <span className="paindname">{d.n}</span>
          <span className="paindleg">{d.leg}</span>
        </span>
      </div>
    </div>
  );
}
